import Backbone from 'backbone';
import * as d3 from 'd3';
import $ from 'jquery';
import React from 'react';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {ResidueType} from 'biojs/BioConstants';
import {ANALYTICS_MODULE, ACTION_CATEGORY} from 'constants/AnalyticsConstants';
import * as colors from 'lib/Colors';
import AddNewFeatureForm from 'molbio/feature-libraries/AddNewFeatureForm';
import {Feature as ComponentFeature, Model as FeatureLibrary} from 'molbio/feature-libraries/FeatureLibrary';
import {
  deleteFeaturesFromLibrary,
  moveFeaturesToLibrary,
  updateFeatures,
} from 'molbio/feature-libraries/FeatureLibraryActions';
import {Feature} from 'molbio/feature-libraries/FeatureLibraryConstants';
import FeatureTable from 'molbio/feature-libraries/FeatureTable';
import notifications from 'notifications';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import {BasicEvent} from 'util/Types';

export interface LibraryViewOptions extends Backbone.ViewOptions<FeatureLibrary> {
  libraryMember: typeof ComponentFeature;
  collectionName: string;
}

class LibraryView extends Backbone.View<FeatureLibrary> {
  library!: FeatureLibrary;
  libraryMember!: typeof ComponentFeature;
  collectionName!: string;
  comparator!: string;
  reverse!: boolean;
  members!: Backbone.Collection<ComponentFeature>;

  static initClass(): void {
    this.prototype.el = '.library-view';
    this.prototype.events = {
      // @ts-expect-error: Backbone events can be either the events hash or a method returning the events hash
      // The Backbone types do not know about this.
      'click .new-library-member .color-chooser li': 'chooseColor',
      'click th span.comparator': 'handleSort',
      'submit form.create-feature': 'createLibraryMember',
    };
  }

  initialize(options: LibraryViewOptions): void {
    this.libraryMember = options.libraryMember;
    this.collectionName = options.collectionName;
    this.comparator = 'name';
    this.reverse = false;
  }

  die(): void {
    this.stopListening();
    this.undelegateEvents();
  }

  chooseColor(e: BasicEvent): void {
    const $target = $(e.target);
    $target.parent().find('.active').removeClass('active');
    $target.addClass('active');
  }

  createFeature(newFeature: Partial<Feature>): void {
    // eslint-disable-next-line new-cap
    const member = new this.libraryMember(newFeature);
    const invalid = member.validate(member.toJSON());
    if (invalid) {
      notifications.error(invalid);
      return;
    }
    member
      .save()
      .done(() => {
        this.library.get(this.collectionName).add(member);
        this.$el.find('.new-library-member input:text, .new-library-member textarea').val('');
        notifications.success('Item added to folder successfully.');
        this.$el.find('.new-library-member .color-chooser li').eq(0).addClass('active');
        analytics.track('feature_library', 'added_feature', {
          product: ANALYTICS_MODULE.MOLBIO,
          actionCategory: ACTION_CATEGORY.WRITE,
          featureLibraryId: this.library.get('api_identifier'),
          featureId: member.id,
          featureType: member.get('feature_type'),
          matchType: member.get('match_type'),
          name: member.get('name'),
        });
      })
      .fail(notifications.notifyError);
  }

  createLibraryMember(e: BasicEvent): void {
    e.preventDefault();
    const attrs: Partial<Feature> = {
      name: this.$el.find('.new-library-member .name').val(),
      bases: this.$el.find('.new-library-member .bases').val(),
      color: d3
        .rgb(this.$el.find('.new-library-member .color-chooser .active').css('background-color'))
        .toString(),
      match_type: this.$el.find('.match_type:checked').val(),
      library_id: this.library.id,
    };
    const typeNode = this.$el.find('.new-library-member .type');
    if (typeNode.length !== 0) {
      attrs.feature_type = typeNode.val();
    }
    this.createFeature(attrs);
  }

  update(library: FeatureLibrary): void {
    this.library = library;
    this.stopListening();
    this.members = this.library.get(this.collectionName);
    this.listenTo(this.members, 'add remove', this.render.bind(this));
  }

  handleSort(e: BasicEvent): void {
    const comparator = $(e.currentTarget).attr('data-name');
    if (comparator === this.comparator) {
      this.reverse = !this.reverse;
    }
    this.comparator = comparator;
    this.members.comparator = this.comparator;
    // eslint-disable-next-line no-restricted-syntax
    this.members.sort();
    this.render();
  }

  render(): LibraryView {
    this.$el.find('.library-name').text(this.library.get('name')).removeClass('hidden');
    if (this.library.get('permissions').writable) {
      this.$el.find('.new-library-member').removeClass('hidden');
    } else {
      this.$el.find('.new-library-member').addClass('hidden');
    }

    const models = _.clone(this.members.models);
    if (this.reverse) {
      models.reverse();
    }

    const features = this.members.models.map((feature) => feature.attributes);
    const featureCollection = this.members;
    const allMembers = this.members.models;
    renderToDOM(
      <>
        {this.library.get('permissions').writable && (
          <AddNewFeatureForm
            onAddNewFeature={(newFeature) => {
              this.createFeature({
                name: newFeature.name,
                bases: newFeature.residues,
                color: newFeature.color,
                match_type: newFeature.residueType === ResidueType.NUCLEOTIDE ? 'nucleotide' : 'protein',
                feature_type: newFeature.featureType,
                library_id: this.library.id,
              });
            }}
          />
        )}
        <FeatureTable
          isReadOnly={!this.library.get('permissions').writable}
          features={features}
          libraries={this.library.collection.models.map((library) => library.attributes)}
          currentLibraryId={this.library.get('api_identifier')}
          onUpdateFeature={(feature, field, value) => {
            updateFeatures([{id: feature.id, [field]: value}], this.library.id)
              .fail((res) => {
                notifications.notifyError(res);
              })
              .done(() => {
                this.library.fetch().done(this.render.bind(this));
                notifications.success(`1 feature was saved successfully.`);
                analytics.track('feature_library', 'edited_feature', {
                  product: ANALYTICS_MODULE.MOLBIO,
                  actionCategory: ACTION_CATEGORY.WRITE,
                  featureLibraryId: this.library.get('api_identifier'),
                  featureId: feature.id,
                });
              });
          }}
          onMoveToLibrary={(libraryId, selectedFeatures) => {
            const destinationLibrary = this.library.collection.models.find(
              (m) => m.get('api_identifier') === libraryId
            );
            moveFeaturesToLibrary(
              selectedFeatures.map((feature) => feature.id),
              this.library.id,
              destinationLibrary.id
            )
              .fail(notifications.notifyError)
              .done(() => {
                for (const feature of selectedFeatures) {
                  const member = allMembers.find((m) => m.get('id') === feature.id);
                  if (member) {
                    member.set({
                      library_id: destinationLibrary.id,
                      selected: false,
                    });
                  }
                  analytics.track('feature_library', 'moved_feature', {
                    product: ANALYTICS_MODULE.MOLBIO,
                    actionCategory: ACTION_CATEGORY.WRITE,
                    sourceFeatureLibraryId: this.library.get('api_identifier'),
                    destinationFeatureLibraryId: libraryId,
                    featureId: feature.id,
                  });
                }
                const membersToMove = allMembers.filter((member) =>
                  selectedFeatures.some((feature) => feature.id === member.get('id'))
                );
                featureCollection.remove(membersToMove);
                destinationLibrary.get('features').add(membersToMove);
                notifications.success(
                  <>
                    Successfully moved {selectedFeatures.length} items to{' '}
                    <strong>{destinationLibrary.get('name')}</strong>.
                  </>
                );
              });
          }}
          onDeleteFeatures={(selectedFeatures) => {
            deleteFeaturesFromLibrary(
              selectedFeatures.map((feature) => feature.id),
              this.library.get('id')
            )
              .fail(notifications.notifyError)
              .done(() => {
                const members = allMembers.filter((member) =>
                  selectedFeatures.some((feature) => feature.id === member.get('id'))
                );
                featureCollection.remove(members);
                notifications.success(`Successfully deleted ${selectedFeatures.length} item(s).`);
                selectedFeatures.forEach((feature) => {
                  analytics.track('feature_library', 'deleted_feature', {
                    product: ANALYTICS_MODULE.MOLBIO,
                    actionCategory: ACTION_CATEGORY.WRITE,
                    featureLibraryId: this.library.get('api_identifier'),
                    featureId: feature.id,
                  });
                });
              });
          }}
        />
      </>,
      this.el
    );

    const $colors = this.$el.find('.new-library-member .color-chooser');
    if ($colors.find('li').length === 0) {
      for (const color of colors.pastelColors) {
        const $li = $('<li>').css('background-color', color);
        $colors.append($li);
      }
    }

    this.$el.find('.help').tooltip({
      placement: 'top',
      container: 'body',
    });
    this.$el.find('.new-library-member .color-chooser li').eq(0).addClass('active');
    return this;
  }
}
LibraryView.initClass();

export default LibraryView;
